import React, { useCallback, useMemo, useState, useEffect, useContext } from 'react';
import {
  MaterialReactTable,
  MRT_EditActionButtons,
  type MaterialReactTableProps,
  type MRT_ColumnDef,
  type MRT_Row,
} from 'material-react-table';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { data } from './makeData';
import { useAPI } from "../../../shared/services/api/API";
import { WASHUB_CONSTANTS } from "../../../shared/Constants";
import { useNavigate } from 'react-router-dom';
import { BusinessContext } from '../../../shared/BusinessContext';


export type BusinessDetails = {
  id: string;
  name: string;
  address: string;
  zipCode: string; 
  state: string;
  country: string;
  mail: string;
  phone: string;
  contactName: string;
  contactPhone: string;
};

export const Business = (props: any) => {
  const { httpGet, httpPost, httpPut, httpDelete, handleAlertBar } = useAPI();
  const [tableData, setTableData] = useState<BusinessDetails[]>(() => data);
  const { updateBusList } = useContext(BusinessContext);
  const [validationErrors, setValidationErrors] = useState<{
    [cellId: string]: string;
  }>({});
  const navigate = useNavigate();
  const queryParams = {
    page: 0,
    size: 10000,
    sortColumn: 'modifiedAt',
    isDescending: true,
  };
  
  // GET with API
  const fetchBusiness = async () => {
      httpGet(WASHUB_CONSTANTS.API_URLS.BUSINESS_DATA_GET, queryParams).then((response) => {
       if (response.status === 200) {
          const sortBusinessArray = response?.data?.sort((a: any,b: any) => a?.name.toLowerCase().localeCompare(b.name.toLowerCase()));
          setTableData(sortBusinessArray);
          updateBusList(sortBusinessArray);
       } else {
          if(response.data.error){        
          console.error('Error fetching Business: ', response.data.message);
           setTableData([]);
          }
       }
    })
  };

 useEffect(() => {    
    fetchBusiness();
  }, []); 

  // PUT with API

  const handleSaveRowEdits: MaterialReactTableProps<BusinessDetails>['onEditingRowSave'] =
    async ({ exitEditingMode, row, values }) => {
      if (!Object.keys(validationErrors).length) {
        const payload = {
          name: valuesDetail?.name || row.original.name,
          address: valuesDetail?.address || row.original?.address,
          state: valuesDetail?.state || row.original.state,
          country: valuesDetail?.country ||row.original.country,
          zipCode: valuesDetail?.zipCode || row.original.zipCode,
          mail: valuesDetail?.mail ||row.original.mail,
          phone: valuesDetail?.phone ||row.original?.phone,
          contactName: valuesDetail?.contactName|| row.original.contactPhone,
          contactPhone: valuesDetail?.contactPhone ||row.original.contactPhone,
        };
        httpPut(WASHUB_CONSTANTS.API_URLS.BUSINESS_DATA_PUT+'/'+row.original.id, payload).then((response) => {
          console.log( 'jwtToken :',localStorage.getItem("jwtToken"));
          console.log( 'RESPONSE :',response);
           if (response.status === 200) {
              fetchBusiness();
              handleAlertBar("success", "Business updated sucessfully");
              exitEditingMode(); //required to exit editing mode and close modal
           } else {
              if(response.data.error){        
              console.error('Error Updating Business: ', response.data.message);
              }
           }
        });
      }
    };

  const handleCancelRowEdits = () => {
    setValidationErrors({});
  };

  const handleDeleteRow = useCallback(
    (row: MRT_Row<BusinessDetails>) => {
      if (
        !window.confirm(`Are you sure you want to delete ${row.getValue('firstName')}`)
      ) {
        return;
      }
      //send api delete request here, then refetch or update local table data for re-render
      tableData.splice(row.index, 1);
      setTableData([...tableData]);
    },
    [tableData],
  );

  
  const columns = useMemo<MRT_ColumnDef<BusinessDetails>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 140,
      },
      {
        accessorKey: 'address',
        header: 'Address',
        size: 50,
      },
      {
        accessorKey: 'zipCode',
        header: 'Zipcode',   
        size: 50,     
      },
      {
        accessorKey: 'mail',
        header: 'Email',
      },
      {
        accessorKey: 'state',
        header: 'State',  
        size: 50,      
      },
      {
        accessorKey: 'country',
        header: 'Country', 
        size: 50,       
      },
      {
        accessorKey: 'phone',
        header: 'Phone', 
        size: 50,       
      },
      {
        accessorKey: 'contactName',
        header: 'Contact Name', 
        size: 50,         
      },
      {
        accessorKey: 'contactPhone',
        header: 'Contact Phone',  
        size: 50,        
      },
    ],
    [],
  );

  const createNewBusiness = () => {
    const column = [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 140,
        required: true,
      },
      {
        accessorKey: 'address',
        header: 'Address',
        size: 140,
        required: true,
      },
      {
        accessorKey: 'zipCode',
        header: 'Zipcode', 
        required: true,       
      },
      {
        accessorKey: 'mail',
        header: 'Email',
        required: true,
      },
      {
        accessorKey: 'state',
        header: 'State',  
        required: true,      
      },
      {
        accessorKey: 'country',
        header: 'Country',  
        required: true,      
      },
      {
        accessorKey: 'phone',
        header: 'Phone',  
        required: true,      
      },
      {
        accessorKey: 'contactName',
        header: 'Contact Name',        
      },
      {
        accessorKey: 'contactPhone',
        header: 'Contact Phone',       
      },
    ];
    navigate('/create-business',{
      state:{column}
    });
  }

  const [valuesDetail, setValuesDetail] = useState<any>(() =>
    columns.reduce((acc, column) => {
      acc[column.accessorKey ?? ''] = '';
      return acc;
    }, {} as any),
  );

  return (
    <div style={{padding:"5px 15px", height: "calc(100vh - 12vh)"}} >
          <Grid container justifyContent={'space-between'} className='mb-2 mt-1'>
          <Grid item>
            <Typography sx={{ textAlign: 'start'}} variant='h5'className='mt-1'>Business</Typography>
          </Grid>
          <Grid item>
          <Button
            color="primary"
            sx={{color: 'white'}}
            onClick={createNewBusiness}
            variant="contained"
          >
            Create New Business
          </Button>
          </Grid>
          </Grid>
      <MaterialReactTable
        displayColumnDefOptions={{
          'mrt-row-actions': {
            muiTableHeadCellProps: {
              align: 'center',
            },
            size: 80,
          },
        }}
        muiTableContainerProps= {{
          sx: {
              height: "65vh"
          }
        }}
        columns={columns}
        data={tableData}
        enableFullScreenToggle={false}
        enableStickyHeader
        enableColumnActions={false}
        enableColumnFilters={false}
        enableFilters={true}
        enableSorting={false}
        enableHiding={false}
        enableDensityToggle={false}
        enableToolbarInternalActions={false} 
        enableColumnOrdering={false}
        enableEditing
        initialState={{
          showGlobalFilter: true
        }}
        editDisplayMode="modal"
        onEditingRowSave={handleSaveRowEdits}
        onEditingRowCancel={handleCancelRowEdits}
          renderEditRowDialogContent={({
            table,
            row,
            internalEditComponents,
          }) => (
            <Dialog fullWidth open={true}>
              <DialogTitle
                sx={{ backgroundColor: '#83CBEB', color: '#ffff' }}
                variant="h5"
                textAlign={'center'}
                className="p-3 mb-3"
              >
                Update Business
              </DialogTitle>
              <DialogContent>
                <Grid container spacing={3}>
                  {columns.map((column) => (
                    <>
                      {column.accessorKey === 'name' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={12}
                          lg={12}
                        >
                          <TextField
                            fullWidth
                            key={column.accessorKey}
                            variant="standard"
                            label={column.header}
                            name={column.accessorKey}
                            defaultValue={row.original.name}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          />
                        </Grid>
                      )}
                      {column.accessorKey === 'mail' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={12}
                          lg={12}
                        >
                          <TextField
                            fullWidth
                            key={column.accessorKey}
                            variant="standard"
                            label={column.header}
                            name={column.accessorKey}
                            defaultValue={row.original.mail}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          />
                        </Grid>
                      )}
                      {column.accessorKey === 'phone' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={12}
                          lg={12}
                        >
                          <TextField
                            fullWidth
                            key={column.accessorKey}
                            variant="standard"
                            label={column.header}
                            name={column.accessorKey}
                            defaultValue={row.original.phone}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          />
                        </Grid>
                      )}
                      {column.accessorKey === 'address' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={12}
                          lg={12}
                        >
                          <TextField
                            fullWidth
                            key={column.accessorKey}
                            variant="standard"
                            label={column.header}
                            name={column.accessorKey}
                            defaultValue={row.original.address}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          />
                        </Grid>
                      )}
                      {column.accessorKey === 'zipCode' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={12}
                          lg={12}
                        >
                          <TextField
                            fullWidth
                            key={column.accessorKey}
                            variant="standard"
                            label={column.header}
                            name={column.accessorKey}
                            defaultValue={row.original.zipCode}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          />
                        </Grid>
                      )}
                      {column.accessorKey === 'state' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={12}
                          lg={12}
                        >
                          <TextField
                            fullWidth
                            key={column.accessorKey}
                            variant="standard"
                            label={column.header}
                            name={column.accessorKey}
                            defaultValue={row.original.state}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          />
                        </Grid>
                      )}
                      {column.accessorKey === 'country' && (
                        <Grid
                          key={column.accessorKey}
                          item
                          xs={12}
                          md={12}
                          lg={12}
                        >
                          <TextField
                            fullWidth
                            key={column.accessorKey}
                            variant="standard"
                            label={column.header}
                            name={column.accessorKey}
                            defaultValue={row.original.country}
                            onChange={(e) =>
                              setValuesDetail({
                                ...valuesDetail,
                                [e.target.name]: e.target.value,
                              })
                            }
                          />
                        </Grid>
                      )}
                    </>
                  ))}
                </Grid>
              </DialogContent>
              <DialogActions>
                <MRT_EditActionButtons variant="text" table={table} row={row} />
              </DialogActions>
            </Dialog>
          )}
        renderRowActions={({ row, table }) => (
          <Box sx={{ display: 'flex', gap: '1rem' }}>
            <Tooltip arrow placement="left" title="Edit">
              <IconButton onClick={() => table.setEditingRow(row)}>
                <Edit />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      />
    </div>
  );
};

export default Business;